import { supabase } from "../../lib/supabase";

// Log an activity event
export async function logActivity(action, target = {}) {
  try {
    const { data: session } = await supabase.auth.getSession();
    const uid = session?.session?.user?.id;
    if (!uid) return;
    await supabase.from("drive_activity").insert([
      {
        user_id: uid,
        action,
        target_type: target.type || null,
        target_id: target.id || null,
        target_name: target.name || null,
        metadata: target.metadata || null,
      },
    ]);
  } catch (e) {
    console.warn("activity log failed", e);
  }
}

export async function fetchFolders() {
  const { data, error } = await supabase
    .from("drive_folders")
    .select("*")
    .order("name");
  if (error) throw error;
  return data;
}

export async function fetchFiles(folderId) {
  const q = supabase.from("drive_files").select("*").order("uploaded_at", { ascending: false });
  const { data, error } = folderId ? await q.eq("folder_id", folderId) : await q;
  if (error) throw error;
  return data;
}

export async function createFolder({ name, parent_id, scope = "company" }) {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const { data, error } = await supabase
    .from("drive_folders")
    .insert([{ name, slug, parent_id, scope }])
    .select()
    .single();
  if (error) throw error;
  await logActivity("create_folder", { type: "folder", id: data.id, name: data.name });
  return data;
}

export async function uploadFile(folderId, file) {
  const timestamp = Date.now();
  const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `${folderId}/${timestamp}-${safe}`;
  const { error: upErr } = await supabase.storage.from("drive-files").upload(path, file, {
    cacheControl: "3600",
    upsert: false,
  });
  if (upErr) throw upErr;

  const now = new Date();
  const fileMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const { data, error } = await supabase
    .from("drive_files")
    .insert([
      {
        folder_id: folderId,
        storage_path: path,
        name: file.name,
        size: file.size,
        mime_type: file.type,
        file_month: fileMonth,
      },
    ])
    .select()
    .single();
  if (error) throw error;
  await logActivity("upload_file", { type: "file", id: data.id, name: data.name });
  return data;
}

export async function downloadFile(filePath, filename) {
  const { data, error } = await supabase.storage.from("drive-files").createSignedUrl(filePath, 60);
  if (error) throw error;
  const link = document.createElement("a");
  link.href = data.signedUrl;
  link.download = filename || "download";
  document.body.appendChild(link);
  link.click();
  link.remove();
  await logActivity("download_file", { type: "file", name: filename });
}

export async function deleteFile(fileRow) {
  const { error: storageErr } = await supabase.storage.from("drive-files").remove([fileRow.storage_path]);
  if (storageErr) console.warn("storage remove", storageErr);
  const { error } = await supabase.from("drive_files").delete().eq("id", fileRow.id);
  if (error) throw error;
  await logActivity("delete_file", { type: "file", id: fileRow.id, name: fileRow.name });
}

export async function fetchActivity(limit = 20) {
  const { data, error } = await supabase
    .from("drive_activity")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data;
}

export async function fetchArtists() {
  const { data, error } = await supabase.from("drive_artists").select("*").order("artist_name");
  if (error) throw error;
  return data;
}

export async function fetchFeatures() {
  const { data, error } = await supabase.from("drive_features").select("*").order("artist_name");
  if (error) throw error;
  return data || [];
}

export async function fetchCreators() {
  const { data, error } = await supabase.from("drive_creators").select("*").order("creator_name");
  if (error) throw error;
  return data || [];
}

export async function deleteCreator(id, { withFiles = false } = {}) {
  const { data: contracts } = await supabase.from("drive_contracts").select("id, storage_path, signed_storage_path").eq("creator_id", id);
  const { data: files } = await supabase.from("drive_files").select("id, storage_path").eq("creator_id", id);
  if (withFiles) {
    const paths = [
      ...(contracts || []).flatMap((c) => [c.storage_path, c.signed_storage_path]),
      ...(files || []).map((f) => f.storage_path),
    ].filter(Boolean);
    if (paths.length > 0) await supabase.storage.from("drive-files").remove(paths);
    if (contracts && contracts.length > 0) await supabase.from("drive_contracts").delete().eq("creator_id", id);
    if (files && files.length > 0) await supabase.from("drive_files").delete().eq("creator_id", id);
  } else {
    if (contracts && contracts.length > 0) await supabase.from("drive_contracts").update({ creator_id: null }).eq("creator_id", id);
    if (files && files.length > 0) await supabase.from("drive_files").update({ creator_id: null }).eq("creator_id", id);
  }
  const { error } = await supabase.from("drive_creators").delete().eq("id", id);
  if (error) throw error;
  await logActivity("delete_creator", { type: "creator", id, metadata: { withFiles } });
}

export async function deleteFeature(id, { withFiles = false } = {}) {
  // Related contracts & files
  const { data: contracts } = await supabase.from("drive_contracts").select("id, storage_path, signed_storage_path").eq("feature_id", id);
  const { data: files } = await supabase.from("drive_files").select("id, storage_path").eq("feature_id", id);
  if (withFiles) {
    const paths = [
      ...(contracts || []).flatMap((c) => [c.storage_path, c.signed_storage_path]),
      ...(files || []).map((f) => f.storage_path),
    ].filter(Boolean);
    if (paths.length > 0) await supabase.storage.from("drive-files").remove(paths);
    if (contracts && contracts.length > 0) await supabase.from("drive_contracts").delete().eq("feature_id", id);
    if (files && files.length > 0) await supabase.from("drive_files").delete().eq("feature_id", id);
  } else {
    if (contracts && contracts.length > 0) await supabase.from("drive_contracts").update({ feature_id: null }).eq("feature_id", id);
    if (files && files.length > 0) await supabase.from("drive_files").update({ feature_id: null }).eq("feature_id", id);
  }
  const { error } = await supabase.from("drive_features").delete().eq("id", id);
  if (error) throw error;
  await logActivity("delete_feature", { type: "feature", id, metadata: { withFiles } });
}

export async function fetchCustomers() {
  const { data, error } = await supabase.from("drive_customers").select("*").order("name");
  if (error) throw error;
  return data;
}

export async function createArtist(payload) {
  const { data, error } = await supabase.from("drive_artists").insert([payload]).select().single();
  if (error) throw error;
  await logActivity("create_artist", { type: "artist", id: data.id, name: data.artist_name });
  return data;
}

export async function createCustomer(payload) {
  const { data, error } = await supabase.from("drive_customers").insert([payload]).select().single();
  if (error) throw error;
  await logActivity("create_customer", { type: "customer", id: data.id, name: data.name });
  return data;
}

export async function updateArtist(id, patch) {
  const { data, error } = await supabase.from("drive_artists").update(patch).eq("id", id).select().single();
  if (error) throw error;
  await logActivity("update_artist", { type: "artist", id, name: data.artist_name });
  return data;
}

export async function deleteArtist(id, { withFiles = false } = {}) {
  // Fetch related contracts to also delete their storage files (if withFiles)
  const { data: contracts } = await supabase.from("drive_contracts").select("id, storage_path, file_id").eq("artist_id", id);

  if (withFiles && contracts && contracts.length > 0) {
    const paths = contracts.map((c) => c.storage_path).filter(Boolean);
    if (paths.length > 0) {
      await supabase.storage.from("drive-files").remove(paths);
    }
    const fileIds = contracts.map((c) => c.file_id).filter(Boolean);
    if (fileIds.length > 0) {
      await supabase.from("drive_files").delete().in("id", fileIds);
    }
    await supabase.from("drive_contracts").delete().eq("artist_id", id);
  } else if (!withFiles && contracts && contracts.length > 0) {
    // Detach: set artist_id null so files stay reachable
    await supabase.from("drive_contracts").update({ artist_id: null }).eq("artist_id", id);
  }

  const { error } = await supabase.from("drive_artists").delete().eq("id", id);
  if (error) throw error;
  await logActivity("delete_artist", { type: "artist", id, metadata: { withFiles, contractCount: contracts?.length || 0 } });
}

// Customer variants
export async function updateCustomer(id, patch) {
  const { data, error } = await supabase.from("drive_customers").update(patch).eq("id", id).select().single();
  if (error) throw error;
  await logActivity("update_customer", { type: "customer", id, name: data.name });
  return data;
}

export async function deleteCustomer(id, { withFiles = false } = {}) {
  const { data: contracts } = await supabase.from("drive_contracts").select("id, storage_path, file_id").eq("customer_id", id);
  if (withFiles && contracts && contracts.length > 0) {
    const paths = contracts.map((c) => c.storage_path).filter(Boolean);
    if (paths.length > 0) await supabase.storage.from("drive-files").remove(paths);
    const fileIds = contracts.map((c) => c.file_id).filter(Boolean);
    if (fileIds.length > 0) await supabase.from("drive_files").delete().in("id", fileIds);
    await supabase.from("drive_contracts").delete().eq("customer_id", id);
  } else if (!withFiles && contracts && contracts.length > 0) {
    await supabase.from("drive_contracts").update({ customer_id: null }).eq("customer_id", id);
  }
  const { error } = await supabase.from("drive_customers").delete().eq("id", id);
  if (error) throw error;
  await logActivity("delete_customer", { type: "customer", id, metadata: { withFiles } });
}

// Notes (Timeline)
export async function fetchNotes({ artist_id, customer_id, external_id }) {
  let q = supabase.from("drive_notes").select("*").order("created_at", { ascending: false });
  if (artist_id) q = q.eq("artist_id", artist_id);
  if (customer_id) q = q.eq("customer_id", customer_id);
  if (external_id) q = q.eq("external_id", external_id);
  const { data, error } = await q;
  if (error) throw error;
  return data;
}

export async function addNote({ artist_id = null, customer_id = null, external_id = null, body }) {
  const { data, error } = await supabase.from("drive_notes").insert([{ artist_id, customer_id, external_id, body }]).select().single();
  if (error) throw error;
  await logActivity("add_note", { type: "note", id: data.id, name: body.slice(0, 60) });
  return data;
}

export async function deleteNote(id) {
  const { error } = await supabase.from("drive_notes").delete().eq("id", id);
  if (error) throw error;
}

export async function countArtistContracts(artist_id) {
  const { count } = await supabase.from("drive_contracts").select("*", { count: "exact", head: true }).eq("artist_id", artist_id);
  return count || 0;
}

export async function countCustomerContracts(customer_id) {
  const { count } = await supabase.from("drive_contracts").select("*", { count: "exact", head: true }).eq("customer_id", customer_id);
  return count || 0;
}

export async function fetchContractsForContact({ artist_id, customer_id, external_id }) {
  let q = supabase.from("drive_contracts").select("*").order("created_at", { ascending: false });
  if (artist_id) q = q.eq("artist_id", artist_id);
  if (customer_id) q = q.eq("customer_id", customer_id);
  if (external_id) q = q.eq("external_id", external_id);
  const { data, error } = await q;
  if (error) throw error;
  return data;
}

export function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(1) + " " + sizes[i];
}

// ═══════════════════════════════════════════════════════════════
// EXTERNE (dritte CRM-Kategorie)
// ═══════════════════════════════════════════════════════════════
export async function fetchExternals() {
  const { data, error } = await supabase.from("drive_externals").select("*").order("full_name");
  if (error) throw error;
  return data;
}
export async function createExternal(payload) {
  const { data, error } = await supabase.from("drive_externals").insert([payload]).select().single();
  if (error) throw error;
  await logActivity("create_external", { type: "external", id: data.id, name: data.full_name });
  return data;
}
export async function updateExternal(id, patch) {
  const { data, error } = await supabase.from("drive_externals").update(patch).eq("id", id).select().single();
  if (error) throw error;
  await logActivity("update_external", { type: "external", id, name: data.full_name });
  return data;
}
export async function deleteExternal(id, { withFiles = false } = {}) {
  const { data: contracts } = await supabase.from("drive_contracts").select("id, storage_path").eq("external_id", id);
  if (withFiles && contracts && contracts.length > 0) {
    const paths = contracts.map((c) => c.storage_path).filter(Boolean);
    if (paths.length > 0) await supabase.storage.from("drive-files").remove(paths);
    await supabase.from("drive_contracts").delete().eq("external_id", id);
  } else if (!withFiles && contracts && contracts.length > 0) {
    await supabase.from("drive_contracts").update({ external_id: null }).eq("external_id", id);
  }
  const { error } = await supabase.from("drive_externals").delete().eq("id", id);
  if (error) throw error;
  await logActivity("delete_external", { type: "external", id, metadata: { withFiles } });
}
export async function countExternalContracts(external_id) {
  const { count } = await supabase.from("drive_contracts").select("*", { count: "exact", head: true }).eq("external_id", external_id);
  return count || 0;
}

// ═══════════════════════════════════════════════════════════════
// FOLDER DELETE
// ═══════════════════════════════════════════════════════════════
export async function deleteFolder(folderId, { withContent = false } = {}) {
  if (withContent) {
    // Alle Files & Subfolders rekursiv sammeln und löschen
    const { data: allFolders } = await supabase.from("drive_folders").select("id, parent_id");
    const toDelete = new Set([folderId]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const f of allFolders || []) {
        if (f.parent_id && toDelete.has(f.parent_id) && !toDelete.has(f.id)) {
          toDelete.add(f.id); changed = true;
        }
      }
    }
    const ids = Array.from(toDelete);
    const { data: files } = await supabase.from("drive_files").select("id, storage_path").in("folder_id", ids);
    const paths = (files || []).map((f) => f.storage_path).filter(Boolean);
    if (paths.length > 0) await supabase.storage.from("drive-files").remove(paths);
    if (files && files.length > 0) await supabase.from("drive_files").delete().in("id", files.map((f) => f.id));
    await supabase.from("drive_folders").delete().in("id", ids);
  } else {
    // Nur leerer Ordner: prüfen
    const { count: fileCount } = await supabase.from("drive_files").select("*", { count: "exact", head: true }).eq("folder_id", folderId);
    const { count: subCount } = await supabase.from("drive_folders").select("*", { count: "exact", head: true }).eq("parent_id", folderId);
    if ((fileCount || 0) + (subCount || 0) > 0) {
      throw new Error(`Ordner ist nicht leer (${fileCount || 0} Dateien, ${subCount || 0} Unterordner). Wähle „mit Inhalt löschen".`);
    }
    const { error } = await supabase.from("drive_folders").delete().eq("id", folderId);
    if (error) throw error;
  }
  await logActivity("delete_folder", { type: "folder", id: folderId, metadata: { withContent } });
}
